/**
 * Firebase web SDK config — sourced from Vite env vars per environment file.
 * @returns {import('firebase/app').FirebaseOptions | null}
 */

/** Hosts allowed as Firebase Auth `authDomain` (must match Console authorized domains). */
const AUTH_DOMAIN_HOSTS = new Set([
  'alea-tui.com',
  'www.alea-tui.com',
  'alea-tui.live',
  'www.alea-tui.live',
  'alea-tui-platform.web.app',
  'alea-tui-platform.firebaseapp.com',
]);

/**
 * Prefer the page hostname as authDomain so Google popup stays same-site and
 * avoids Cross-Origin-Opener-Policy noise on window.closed.
 * Keep the configured domain on localhost / emulators.
 */
export function resolveAuthDomain(configured: string | undefined): string {
  const fallback = String(configured ?? '').trim();
  if (typeof window === 'undefined') return fallback;
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return fallback;
  if (AUTH_DOMAIN_HOSTS.has(host)) return host;
  // Firebase Hosting preview channels: project--channel-id.web.app
  if (host.endsWith('.web.app') && host.startsWith('alea-tui-platform')) return host;
  return fallback;
}

export function getFirebaseConfig() {
  const apiKey = import.meta.env.VITE_FIREBASE_API_KEY?.trim();
  if (!apiKey) return null;

  return {
    apiKey,
    authDomain: resolveAuthDomain(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN),
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || undefined,
  };
}

export function hasFirebaseConfig() {
  return getFirebaseConfig() !== null;
}
